import { Avatar, Box, Skeleton, Typography } from '@mui/material';
import { formatDate } from '../../../../../shared/utils/dateFormatting';

interface ActivityEvent {
  eventId: string;
  eventType: string;
  timestamp: string;
  actorUserId: string;
  actorUserName: string;
  description: string;
}

interface ActivityTimelineProps {
  events: ActivityEvent[];
  isLoading?: boolean;
}

// Deterministic colour from actorUserId — same person always same colour
const AVATAR_COLOURS = [
  'primary.dark',
  'secondary.dark',
  'success.dark',
  'warning.dark',
  'error.dark',
  'info.dark',
] as const;

function hashUserId(userId: string): number {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = (hash * 31 + userId.charCodeAt(i)) >>> 0;
  }
  return hash % AVATAR_COLOURS.length;
}

function getAvatarColour(userId: string): string {
  return AVATAR_COLOURS[hashUserId(userId)];
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

const BADGE_COLOURS: Record<string, string> = {
  AllocationBooked: 'info.main',
  AllocationRebooked: 'info.main',
  AllocationResubmitted: 'info.main',
  AllocationApproved: 'success.main',
  AllocationRejected: 'error.main',
  AllocationCancelled: 'warning.main',
  AllocationAdminCancelled: 'error.main',
};

function getBadgeColour(eventType: string): string {
  return BADGE_COLOURS[eventType] ?? 'text.disabled';
}

export interface ActivityTimelineItemProps {
  event: ActivityEvent;
  isLast: boolean;
}

export function ActivityTimelineItem({
  event,
  isLast,
}: ActivityTimelineItemProps) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5 }}>
      {/* Avatar column with connecting line */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <Avatar
            sx={{
              width: 28,
              height: 28,
              bgcolor: getAvatarColour(event.actorUserId),
              fontSize: '0.625rem',
              fontWeight: 600,
              color: 'common.white',
            }}
          >
            {getInitials(event.actorUserName)}
          </Avatar>
          {/* Status badge */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: getBadgeColour(event.eventType),
              border: '1.5px solid',
              borderColor: 'background.paper',
            }}
          />
        </Box>
        {/* Connecting line — hidden on last item */}
        {!isLast && (
          <Box
            sx={{
              width: '1px',
              flex: 1,
              minHeight: 12,
              bgcolor: 'text.disabled',
              opacity: 0.3,
              my: 0.5,
            }}
          />
        )}
      </Box>

      {/* Content */}
      <Box sx={{ pb: isLast ? 0 : 1.5, minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 500, display: 'block', lineHeight: 1.4 }}
          color="text.primary"
        >
          {event.description}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {event.actorUserName} · {formatDate(event.timestamp)}
        </Typography>
      </Box>
    </Box>
  );
}

export function ActivityTimeline({ events, isLoading }: ActivityTimelineProps) {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {Array.from({ length: 3 }).map((_, i) => (
          <Box
            key={i}
            sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}
          >
            <Skeleton
              variant="circular"
              width={28}
              height={28}
              sx={{ flexShrink: 0 }}
            />
            <Box sx={{ flex: 1 }}>
              <Skeleton
                variant="text"
                sx={{ typography: 'caption', width: '75%' }}
              />
              <Skeleton
                variant="text"
                sx={{ typography: 'caption', width: '50%' }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  if (events.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No activity recorded
      </Typography>
    );
  }

  return (
    <Box data-testid="activity-timeline">
      {events.map((event, index) => (
        <ActivityTimelineItem
          key={event.eventId}
          event={event}
          isLast={index === events.length - 1}
        />
      ))}
    </Box>
  );
}

export default ActivityTimeline;
